import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { 
  ArrowLeft, Crown, Sparkles, Star, Sun, Moon, Hash, 
  Compass, TrendingUp, Heart, Zap, Target, RefreshCw,
  Loader2, Lock, ArrowRight, Calendar, Brain, Eye,
  Flame, Droplets, Wind, Mountain, Activity, History,
  ChevronDown, ChevronUp, BarChart3, Clock, Trash2,
  Bell, AlertTriangle, X, TrendingDown, Trophy, Plus,
  Check, Edit3, Award, Printer
} from 'lucide-react';
import { useAuth } from '@/components/AuthContext';
import { 
  RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar,
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip,
  LineChart, Line, Legend
} from 'recharts';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Element colors
const ELEMENT_COLORS = {
  'Feu': { primary: '#f97316', secondary: '#fdba74', icon: Flame },
  'Eau': { primary: '#3b82f6', secondary: '#93c5fd', icon: Droplets },
  'Air': { primary: '#8b5cf6', secondary: '#c4b5fd', icon: Wind },
  'Terre': { primary: '#22c55e', secondary: '#86efac', icon: Mountain }
};

// Attribute names in French
const ATTR_NAMES = {
  intuition: "Intuition",
  action: "Action",
  stabilite: "Stabilité",
  creativite: "Créativité",
  emotion: "Émotion",
  ambition: "Ambition",
  overall: "Global"
};

// Energy Alert Banner Component
const EnergyAlertBanner = ({ alerts, onDismiss, onViewDetails }) => {
  const [expanded, setExpanded] = useState(false);
  
  if (!alerts || alerts.length === 0) return null;
  
  const latestAlert = alerts[0];
  const interpretation = latestAlert?.interpretation;
  const mainChange = interpretation?.main_change;
  
  if (!interpretation) return null;
  
  const isUp = mainChange?.direction === "up";
  const Icon = isUp ? TrendingUp : TrendingDown;
  const bgColor = isUp ? "from-green-500 to-emerald-600" : "from-orange-500 to-red-500";
  const lightBg = isUp ? "bg-green-50 border-green-200" : "bg-orange-50 border-orange-200";
  
  return (
    <motion.div
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      className="mb-6"
    >
      <div className={`rounded-2xl border-2 ${lightBg} overflow-hidden shadow-lg`}>
        {/* Main Banner */}
        <div 
          className={`bg-gradient-to-r ${bgColor} p-4 cursor-pointer`}
          onClick={() => setExpanded(!expanded)}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center backdrop-blur-sm">
                <Icon className="h-6 w-6 text-white" />
              </div>
              <div className="text-white">
                <div className="flex items-center gap-2">
                  <Bell className="h-4 w-4" />
                  <span className="text-sm font-medium opacity-90">Alerte Energie</span>
                </div>
                <h3 className="font-bold text-lg">{interpretation.title}</h3>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                className="text-white hover:bg-white/20 h-8 w-8 p-0"
                onClick={(e) => {
                  e.stopPropagation();
                  onDismiss(latestAlert.alert_id);
                }}
                data-testid="dismiss-alert-btn"
              >
                <X className="h-5 w-5" />
              </Button>
              {expanded ? (
                <ChevronUp className="h-5 w-5 text-white" />
              ) : (
                <ChevronDown className="h-5 w-5 text-white" />
              )}
            </div>
          </div>
        </div>
        
        {/* Expanded Details */}
        <AnimatePresence>
          {expanded && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className="overflow-hidden"
            >
              <div className="p-5 space-y-4">
                {/* Summary */}
                <p className="text-gray-700">{interpretation.summary}</p>
                
                {/* Advice */}
                <div className="p-4 bg-blue-50 rounded-xl border border-blue-200">
                  <div className="flex items-start gap-2">
                    <Sparkles className="h-5 w-5 text-blue-500 mt-0.5" />
                    <div>
                      <p className="font-medium text-blue-800">Conseil</p>
                      <p className="text-blue-700 text-sm">{interpretation.advice}</p>
                    </div>
                  </div>
                </div>
                
                {/* Changes List */}
                {interpretation.all_changes && interpretation.all_changes.length > 0 && (
                  <div className="space-y-2">
                    <p className="text-sm font-medium text-gray-600">Changements détectés :</p>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                      {interpretation.all_changes.map((change, i) => (
                        <div 
                          key={i}
                          className={`p-3 rounded-lg border ${
                            change.direction === 'up' 
                              ? 'bg-green-50 border-green-200' 
                              : 'bg-orange-50 border-orange-200'
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <span className="text-sm font-medium text-gray-700">
                              {ATTR_NAMES[change.attribute] || change.attribute}
                            </span>
                            <span className={`text-sm font-bold ${
                              change.direction === 'up' ? 'text-green-600' : 'text-orange-600'
                            }`}>
                              {change.direction === 'up' ? '+' : ''}{change.diff}%
                            </span>
                          </div>
                          <div className="text-xs text-gray-500 mt-1">
                            {change.previous}% → {change.current}%
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Date info */}
                <div className="text-xs text-gray-400 text-right">
                  Détecté le {latestAlert.date}
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
      
      {/* Other alerts count */}
      {alerts.length > 1 && (
        <p className="text-sm text-gray-500 mt-2 text-center">
          + {alerts.length - 1} autre{alerts.length > 2 ? 's' : ''} alerte{alerts.length > 2 ? 's' : ''}
        </p>
      )}
    </motion.div>
  );
};

// Achievement Celebration Component
const AchievementCelebration = ({ achievement, onClose }) => {
  if (!achievement) return null;
  
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.8 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.8 }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm"
      onClick={onClose}
    >
      <motion.div
        className="bg-white rounded-3xl p-8 max-w-md mx-4 text-center shadow-2xl"
        onClick={(e) => e.stopPropagation()}
        initial={{ y: 50 }}
        animate={{ y: 0 }}
      >
        <motion.div
          animate={{ scale: [1, 1.2, 1], rotate: [0, 10, -10, 0] }}
          transition={{ duration: 0.5, repeat: 2 }}
          className="mb-6"
        >
          <div className="w-24 h-24 mx-auto bg-gradient-to-br from-amber-400 via-yellow-500 to-orange-500 rounded-full flex items-center justify-center shadow-lg">
            <Trophy className="h-12 w-12 text-white" />
          </div>
        </motion.div>
        
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Objectif Atteint !</h2>
        <p className="text-xl text-purple-600 font-semibold mb-4">
          {achievement.attribute_name}: {achievement.achieved_value}%
        </p>
        <p className="text-gray-600 mb-6 text-lg">{achievement.message}</p>
        
        <div className="flex items-center justify-center gap-2 mb-6">
          <span className="text-sm text-gray-500">Objectif:</span>
          <span className="text-sm font-medium text-gray-700">{achievement.target_value}%</span>
          <span className="text-sm text-gray-500">→</span>
          <span className="text-sm font-bold text-green-600">{achievement.achieved_value}%</span>
          {achievement.exceeded_by > 0 && (
            <span className="px-2 py-0.5 bg-green-100 text-green-700 rounded-full text-xs font-medium">
              +{achievement.exceeded_by}%
            </span>
          )}
        </div>
        
        <Button
          onClick={onClose}
          className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white px-8 py-3 rounded-xl font-semibold"
        >
          <Sparkles className="h-5 w-5 mr-2" />
          Continuer
        </Button>
      </motion.div>
    </motion.div>
  );
};

// Energy Goals Panel Component
const EnergyGoalsPanel = ({ goals, achievements, currentScores, onCreateGoal, onDeleteGoal, onRefreshGoals }) => {
  const [showForm, setShowForm] = useState(false);
  const [newGoal, setNewGoal] = useState({ attribute: '', target_value: 70 });
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const availableAttributes = [
    { key: 'intuition', name: 'Intuition', icon: Eye },
    { key: 'action', name: 'Action', icon: Zap },
    { key: 'stabilite', name: 'Stabilité', icon: Mountain },
    { key: 'creativite', name: 'Créativité', icon: Sparkles },
    { key: 'emotion', name: 'Émotion', icon: Heart },
    { key: 'ambition', name: 'Ambition', icon: Target },
    { key: 'overall', name: 'Global', icon: Activity }
  ];
  
  // Filter out attributes that already have active goals
  const usedAttributes = goals?.map(g => g.attribute) || [];
  const availableForGoal = availableAttributes.filter(a => !usedAttributes.includes(a.key));
  
  const handleSubmit = async () => {
    if (!newGoal.attribute || !newGoal.target_value) return;
    
    setIsSubmitting(true);
    await onCreateGoal(newGoal);
    setNewGoal({ attribute: '', target_value: 70 });
    setShowForm(false);
    setIsSubmitting(false);
  };
  
  return (
    <div className="bg-white rounded-2xl border border-gray-200 p-6 shadow-lg">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="p-3 bg-gradient-to-br from-amber-100 to-orange-100 rounded-xl">
            <Trophy className="h-6 w-6 text-amber-600" />
          </div>
          <div>
            <h3 className="text-xl font-bold text-gray-900">Mes Objectifs</h3>
            <p className="text-sm text-gray-500">{goals?.length || 0} objectif{(goals?.length || 0) > 1 ? 's' : ''} actif{(goals?.length || 0) > 1 ? 's' : ''}</p>
          </div>
        </div>
        {availableForGoal.length > 0 && !showForm && (
          <Button
            onClick={() => setShowForm(true)}
            size="sm"
            className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white"
            data-testid="add-goal-btn"
          >
            <Plus className="h-4 w-4 mr-1" />
            Ajouter
          </Button>
        )}
      </div>
      
      {/* New Goal Form */}
      <AnimatePresence>
        {showForm && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="mb-4 overflow-hidden"
          >
            <div className="p-4 bg-gradient-to-r from-amber-50 to-orange-50 rounded-xl border border-amber-200">
              <p className="text-sm font-medium text-amber-800 mb-3">Nouvel objectif</p>
              
              <div className="grid grid-cols-2 gap-3 mb-3">
                {availableForGoal.map((attr) => {
                  const Icon = attr.icon;
                  const isSelected = newGoal.attribute === attr.key;
                  return (
                    <button
                      key={attr.key}
                      onClick={() => setNewGoal(prev => ({ ...prev, attribute: attr.key }))}
                      className={`p-3 rounded-lg border-2 flex items-center gap-2 transition-all ${
                        isSelected 
                          ? 'border-amber-500 bg-amber-100' 
                          : 'border-gray-200 bg-white hover:border-amber-300'
                      }`}
                    >
                      <Icon className={`h-4 w-4 ${isSelected ? 'text-amber-600' : 'text-gray-500'}`} />
                      <span className={`text-sm font-medium ${isSelected ? 'text-amber-800' : 'text-gray-700'}`}>
                        {attr.name}
                      </span>
                      {isSelected && <Check className="h-4 w-4 text-amber-600 ml-auto" />}
                    </button>
                  );
                })}
              </div>
              
              <div className="mb-4">
                <label className="block text-sm font-medium text-amber-800 mb-2">
                  Objectif: {newGoal.target_value}%
                </label>
                <input
                  type="range"
                  min="20"
                  max="100"
                  step="5"
                  value={newGoal.target_value}
                  onChange={(e) => setNewGoal(prev => ({ ...prev, target_value: parseInt(e.target.value) }))}
                  className="w-full h-2 bg-amber-200 rounded-lg appearance-none cursor-pointer"
                />
                <div className="flex justify-between text-xs text-amber-600 mt-1">
                  <span>20%</span>
                  <span>100%</span>
                </div>
              </div>
              
              <div className="flex gap-2">
                <Button
                  onClick={handleSubmit}
                  disabled={!newGoal.attribute || isSubmitting}
                  className="flex-1 bg-amber-500 hover:bg-amber-600 text-white"
                  data-testid="submit-goal-btn"
                >
                  {isSubmitting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Créer'}
                </Button>
                <Button
                  onClick={() => setShowForm(false)}
                  variant="outline"
                  className="border-amber-300 text-amber-700"
                >
                  Annuler
                </Button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      
      {/* Goals List */}
      {goals && goals.length > 0 ? (
        <div className="space-y-3">
          {goals.map((goal) => {
            const current = currentScores?.[goal.attribute] || 0;
            const progress = Math.min(100, (current / goal.target_value) * 100);
            const isClose = progress >= 80 && progress < 100;
            const AttrIcon = availableAttributes.find(a => a.key === goal.attribute)?.icon || Target;
            
            return (
              <motion.div
                key={goal.goal_id}
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                className={`p-4 rounded-xl border-2 ${
                  isClose 
                    ? 'bg-gradient-to-r from-amber-50 to-yellow-50 border-amber-300' 
                    : 'bg-gray-50 border-gray-200'
                }`}
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <AttrIcon className={`h-5 w-5 ${isClose ? 'text-amber-600' : 'text-gray-500'}`} />
                    <span className="font-medium text-gray-800">{goal.attribute_name}</span>
                    {isClose && (
                      <span className="px-2 py-0.5 bg-amber-200 text-amber-800 rounded-full text-xs font-medium">
                        Presque !
                      </span>
                    )}
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-8 w-8 p-0 text-gray-400 hover:text-red-500"
                    onClick={() => onDeleteGoal(goal.goal_id)}
                    data-testid={`delete-goal-${goal.goal_id}`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
                
                <div className="flex items-center gap-3">
                  <div className="flex-1 h-3 bg-gray-200 rounded-full overflow-hidden">
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{ width: `${progress}%` }}
                      transition={{ duration: 0.5, ease: "easeOut" }}
                      className={`h-full rounded-full ${
                        isClose 
                          ? 'bg-gradient-to-r from-amber-400 to-yellow-500' 
                          : 'bg-gradient-to-r from-purple-400 to-indigo-500'
                      }`}
                    />
                  </div>
                  <div className="text-right min-w-[80px]">
                    <span className={`font-bold ${isClose ? 'text-amber-600' : 'text-purple-600'}`}>
                      {current}%
                    </span>
                    <span className="text-gray-400 mx-1">/</span>
                    <span className="text-gray-600">{goal.target_value}%</span>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      ) : (
        <div className="text-center py-6 text-gray-500">
          <Target className="h-10 w-10 mx-auto mb-2 opacity-50" />
          <p>Aucun objectif défini</p>
          <p className="text-sm">Crée ton premier objectif pour suivre ta progression</p>
        </div>
      )}
      
      {/* Recent Achievements */}
      {achievements && achievements.length > 0 && (
        <div className="mt-6 pt-4 border-t border-gray-200">
          <div className="flex items-center gap-2 mb-3">
            <Award className="h-5 w-5 text-amber-500" />
            <span className="font-medium text-gray-700">Succès récents</span>
          </div>
          <div className="space-y-2">
            {achievements.slice(0, 3).map((ach, i) => (
              <div 
                key={i}
                className="flex items-center gap-3 p-2 bg-gradient-to-r from-amber-50 to-yellow-50 rounded-lg"
              >
                <div className="w-8 h-8 bg-amber-400 rounded-full flex items-center justify-center">
                  <Trophy className="h-4 w-4 text-white" />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-800">{ach.attribute_name}</p>
                  <p className="text-xs text-amber-600">{ach.achieved_value}% atteint</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// Loading Component
const LoadingState = () => (
  <div className="flex flex-col items-center justify-center py-20">
    <motion.div
      animate={{ rotate: 360 }}
      transition={{ duration: 3, repeat: Infinity, ease: "linear" }}
      className="mb-6"
    >
      <div className="relative">
        <Sun className="h-16 w-16 text-amber-500" />
        <Moon className="h-8 w-8 text-indigo-500 absolute -bottom-1 -right-1" />
      </div>
    </motion.div>
    <p className="text-gray-600 text-lg">Oracle consulte les astres...</p>
    <p className="text-gray-400 text-sm mt-2">Analyse transversale en cours</p>
  </div>
);

// Premium Upsell Component
const PremiumUpsell = ({ onNavigate }) => (
  <motion.div
    initial={{ opacity: 0, scale: 0.95 }}
    animate={{ opacity: 1, scale: 1 }}
    className="max-w-lg mx-auto"
  >
    <div className="bg-gradient-to-br from-violet-50 via-purple-50 to-indigo-50 rounded-3xl border-2 border-purple-200 p-8 text-center shadow-2xl">
      <div className="inline-flex items-center justify-center w-24 h-24 bg-gradient-to-br from-violet-500 via-purple-500 to-indigo-600 rounded-full mb-6 shadow-xl">
        <Crown className="h-12 w-12 text-white" />
      </div>
      
      <h2 className="text-2xl font-bold text-gray-900 mb-3">
        Analyse Transversale
      </h2>
      
      <p className="text-gray-600 mb-6 max-w-md mx-auto">
        Cette fonctionnalite exclusive combine toutes vos donnees pour une vision complete de votre parcours.
        Reservee aux membres <strong>REVELIAE Integral</strong>.
      </p>
      
      <div className="grid grid-cols-2 gap-3 mb-8">
        {[
          { icon: Sun, text: "Profil astrologique", color: "text-amber-500" },
          { icon: Hash, text: "Chemin numerique", color: "text-emerald-500" },
          { icon: Moon, text: "Cycles personnels", color: "text-indigo-500" },
          { icon: Sparkles, text: "Synthese IA", color: "text-purple-500" }
        ].map((item, i) => (
          <div key={i} className="flex items-center gap-2 p-3 bg-white/70 rounded-xl">
            <item.icon className={`h-5 w-5 ${item.color}`} />
            <span className="text-sm text-gray-700">{item.text}</span>
          </div>
        ))}
      </div>
      
      <Button
        onClick={() => onNavigate('/premium')}
        className="w-full bg-gradient-to-r from-violet-600 via-purple-600 to-indigo-600 hover:from-violet-700 hover:via-purple-700 hover:to-indigo-700 text-white px-8 py-4 rounded-xl font-semibold shadow-lg"
        data-testid="upgrade-integral-btn"
      >
        <Crown className="h-5 w-5 mr-2" />
        Passer a REVELIAE Integral
        <ArrowRight className="h-5 w-5 ml-2" />
      </Button>
      
      <p className="text-xs text-gray-500 mt-4">
        9.99EUR/mois ou essai 7 jours a 1.99EUR
      </p>
    </div>
  </motion.div>
);

// Profile Prompt
const ProfilePrompt = ({ message, onNavigate }) => (
  <motion.div
    initial={{ opacity: 0, scale: 0.95 }}
    animate={{ opacity: 1, scale: 1 }}
    className="max-w-md mx-auto"
  >
    <div className="bg-gradient-to-br from-sky-50 to-cyan-50 rounded-3xl border-2 border-sky-200 p-8 text-center shadow-xl">
      <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-sky-400 to-cyan-500 rounded-full mb-6 shadow-lg">
        <Calendar className="h-10 w-10 text-white" />
      </div>
      
      <h2 className="text-2xl font-bold text-gray-900 mb-3">Profil Incomplet</h2>
      <p className="text-gray-600 mb-6">{message}</p>
      
      <Button
        onClick={() => onNavigate('/profile')}
        className="bg-gradient-to-r from-sky-500 to-cyan-500 hover:from-sky-600 hover:to-cyan-600 text-white px-8 py-3 rounded-xl font-semibold"
      >
        Completer mon profil
        <ArrowRight className="h-5 w-5 ml-2" />
      </Button>
    </div>
  </motion.div>
);

// Evolution Comparison Chart
const EvolutionChart = ({ history }) => {
  if (!history || history.length < 2) return null;
  
  // Prepare data for the line chart
  const chartData = history.slice().reverse().map(entry => ({
    month: entry.month?.slice(5) || entry.date?.slice(3, 5) || '??',
    fullDate: entry.date,
    intuition: entry.energy_scores?.intuition || 0,
    action: entry.energy_scores?.action || 0,
    stabilite: entry.energy_scores?.stabilite || 0,
    creativite: entry.energy_scores?.creativite || 0,
    emotion: entry.energy_scores?.emotion || 0,
    ambition: entry.energy_scores?.ambition || 0,
    overall: entry.energy_scores?.overall || 0
  }));
  
  return (
    <div className="bg-white rounded-2xl border border-gray-200 p-6 shadow-lg">
      <div className="flex items-center gap-3 mb-4">
        <div className="p-3 bg-gradient-to-br from-indigo-100 to-purple-100 rounded-xl">
          <TrendingUp className="h-6 w-6 text-indigo-600" />
        </div>
        <div>
          <h3 className="text-xl font-bold text-gray-900">Evolution de ton Energie</h3>
          <p className="text-sm text-gray-500">{history.length} analyses sur les derniers mois</p>
        </div>
      </div>
      
      <div className="h-64">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={chartData}>
            <XAxis 
              dataKey="month" 
              axisLine={false}
              tickLine={false}
              tick={{ fill: '#9ca3af', fontSize: 11 }}
            />
            <YAxis 
              domain={[0, 100]} 
              axisLine={false}
              tickLine={false}
              tick={{ fill: '#9ca3af', fontSize: 10 }}
            />
            <Tooltip 
              content={({ active, payload, label }) => {
                if (active && payload && payload.length) {
                  return (
                    <div className="bg-white shadow-lg rounded-lg px-4 py-3 border border-gray-200">
                      <p className="text-sm font-medium text-gray-800 mb-2">
                        {payload[0]?.payload?.fullDate || label}
                      </p>
                      {payload.map((entry, index) => (
                        <p key={index} className="text-xs" style={{ color: entry.color }}>
                          {entry.name}: {entry.value}%
                        </p>
                      ))}
                    </div>
                  );
                }
                return null;
              }}
            />
            <Legend />
            <Line type="monotone" dataKey="overall" name="Global" stroke="#8b5cf6" strokeWidth={3} dot={{ r: 4 }} />
            <Line type="monotone" dataKey="intuition" name="Intuition" stroke="#3b82f6" strokeWidth={1.5} dot={{ r: 2 }} />
            <Line type="monotone" dataKey="action" name="Action" stroke="#f97316" strokeWidth={1.5} dot={{ r: 2 }} />
            <Line type="monotone" dataKey="creativite" name="Creativite" stroke="#a855f7" strokeWidth={1.5} dot={{ r: 2 }} />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

// History List Component
const HistoryList = ({ history, onSelect, onDelete, selectedId }) => {
  if (!history || history.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        <History className="h-12 w-12 mx-auto mb-3 opacity-50" />
        <p>Aucun historique disponible</p>
        <p className="text-sm">Tes analyses seront sauvegardees ici</p>
      </div>
    );
  }
  
  return (
    <div className="space-y-2">
      {history.map((entry) => (
        <motion.div
          key={entry.analysis_id}
          initial={{ opacity: 0, x: -10 }}
          animate={{ opacity: 1, x: 0 }}
          className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${
            selectedId === entry.analysis_id 
              ? 'border-purple-400 bg-purple-50' 
              : 'border-gray-200 bg-white hover:border-purple-200 hover:bg-purple-50/50'
          }`}
          onClick={() => onSelect(entry.analysis_id)}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-violet-100 to-purple-100 rounded-lg flex items-center justify-center">
                <Calendar className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <p className="font-medium text-gray-900">{entry.date}</p>
                <div className="flex items-center gap-2 text-xs text-gray-500">
                  <span>Score global: {entry.energy_scores?.overall || '?'}%</span>
                  {entry.content_summary?.focus && (
                    <span className="px-2 py-0.5 bg-purple-100 text-purple-700 rounded-full">
                      {entry.content_summary.focus}
                    </span>
                  )}
                </div>
              </div>
            </div>
            <Button
              variant="ghost"
              size="sm"
              className="text-gray-400 hover:text-red-500 h-8 w-8 p-0"
              data-testid={`delete-analysis-${entry.analysis_id}`}
              onClick={(e) => {
                e.stopPropagation();
                onDelete(entry.analysis_id);
              }}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </motion.div>
      ))}
    </div>
  );
};

// Radar Chart for Energy Profile
const EnergyRadarChart = ({ profile, energyScores }) => {
  const element = profile?.zodiac?.element || 'Feu';
  
  const data = [
    { attribute: 'Intuition', value: energyScores?.intuition || 50 },
    { attribute: 'Action', value: energyScores?.action || 50 },
    { attribute: 'Stabilite', value: energyScores?.stabilite || 50 },
    { attribute: 'Creativite', value: energyScores?.creativite || 50 },
    { attribute: 'Emotion', value: energyScores?.emotion || 50 },
    { attribute: 'Ambition', value: energyScores?.ambition || 50 },
  ];
  
  const elementConfig = ELEMENT_COLORS[element] || ELEMENT_COLORS['Feu'];
  
  return (
    <div className="bg-white rounded-2xl border border-gray-200 p-6 shadow-lg">
      <div className="flex items-center gap-3 mb-4">
        <div className="p-3 bg-gradient-to-br from-violet-100 to-purple-100 rounded-xl">
          <Activity className="h-6 w-6 text-purple-600" />
        </div>
        <div>
          <h3 className="text-xl font-bold text-gray-900">Profil Energetique</h3>
          <p className="text-sm text-gray-500">Score global: {energyScores?.overall || '?'}%</p>
        </div>
      </div>
      
      <div className="h-72">
        <ResponsiveContainer width="100%" height="100%">
          <RadarChart data={data}>
            <PolarGrid stroke="#e5e7eb" />
            <PolarAngleAxis 
              dataKey="attribute" 
              tick={{ fill: '#6b7280', fontSize: 12 }}
            />
            <PolarRadiusAxis 
              angle={30} 
              domain={[0, 100]} 
              tick={{ fill: '#9ca3af', fontSize: 10 }}
            />
            <Radar
              name="Energie"
              dataKey="value"
              stroke={elementConfig.primary}
              fill={elementConfig.primary}
              fillOpacity={0.3}
              strokeWidth={2}
            />
          </RadarChart>
        </ResponsiveContainer>
      </div>
      
      <div className="flex items-center justify-center gap-2 mt-2">
        <div 
          className="w-4 h-4 rounded-full" 
          style={{ backgroundColor: elementConfig.primary }}
        />
        <span className="text-sm text-gray-600">
          Element dominant: <strong className="text-gray-800">{element}</strong>
        </span>
      </div>
    </div>
  );
};

// Timeline for Personal Year Cycle
const YearCycleTimeline = ({ profile }) => {
  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth();
  const personalYear = profile?.personal_year?.number || 1;
  
  const months = [
    'Jan', 'Fev', 'Mar', 'Avr', 'Mai', 'Juin',
    'Juil', 'Aout', 'Sep', 'Oct', 'Nov', 'Dec'
  ];
  
  const data = months.map((month, index) => {
    const baseEnergy = 40 + (personalYear * 5);
    const monthVariation = Math.sin((index / 12) * Math.PI * 2 + personalYear) * 20;
    const energy = Math.min(100, Math.max(20, baseEnergy + monthVariation));
    
    return {
      month,
      energie: Math.round(energy),
      isCurrent: index === currentMonth
    };
  });
  
  return (
    <div className="bg-white rounded-2xl border border-gray-200 p-6 shadow-lg">
      <div className="flex items-center gap-3 mb-4">
        <div className="p-3 bg-gradient-to-br from-blue-100 to-cyan-100 rounded-xl">
          <BarChart3 className="h-6 w-6 text-blue-600" />
        </div>
        <div>
          <h3 className="text-xl font-bold text-gray-900">Cycle Annuel {currentYear}</h3>
          <p className="text-sm text-gray-500">Annee personnelle {personalYear}</p>
        </div>
      </div>
      
      <div className="h-48">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={data}>
            <defs>
              <linearGradient id="energyGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#8b5cf6" stopOpacity={0.3}/>
                <stop offset="95%" stopColor="#8b5cf6" stopOpacity={0}/>
              </linearGradient>
            </defs>
            <XAxis 
              dataKey="month" 
              axisLine={false}
              tickLine={false}
              tick={{ fill: '#9ca3af', fontSize: 11 }}
            />
            <YAxis domain={[0, 100]} hide />
            <Tooltip 
              content={({ active, payload }) => {
                if (active && payload && payload.length) {
                  return (
                    <div className="bg-white shadow-lg rounded-lg px-3 py-2 border border-gray-200">
                      <p className="text-sm font-medium text-gray-800">
                        {payload[0].payload.month}: <span className="text-purple-600">{payload[0].value}%</span>
                      </p>
                    </div>
                  );
                }
                return null;
              }}
            />
            <Area
              type="monotone"
              dataKey="energie"
              stroke="#8b5cf6"
              strokeWidth={2}
              fill="url(#energyGradient)"
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
      
      <div className="flex items-center justify-center gap-4 mt-2">
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full bg-purple-500" />
          <span className="text-xs text-gray-600">Niveau d'energie</span>
        </div>
        <div className="text-xs text-gray-500">
          Mois actuel: <strong className="text-purple-600">{months[currentMonth]}</strong>
        </div>
      </div>
    </div>
  );
};

// Element Badge Component
const ElementBadge = ({ element }) => {
  const config = ELEMENT_COLORS[element] || ELEMENT_COLORS['Feu'];
  const Icon = config.icon;
  
  return (
    <div 
      className="flex items-center gap-2 px-4 py-2 rounded-full"
      style={{ backgroundColor: `${config.primary}15` }}
    >
      <Icon className="h-4 w-4" style={{ color: config.primary }} />
      <span className="text-sm font-medium" style={{ color: config.primary }}>
        {element}
      </span>
    </div>
  );
};

// Analysis Content Component with Charts
const AnalysisContent = ({ 
  analysis, history, onRefresh, onDelete, onSelectAnalysis, 
  alerts, onDismissAlert, goals, achievements, onCreateGoal, onDeleteGoal 
}) => {
  const { profile, content, user_name, date, energy_scores } = analysis;
  const element = profile?.zodiac?.element || 'Feu';
  const elementConfig = ELEMENT_COLORS[element] || ELEMENT_COLORS['Feu'];
  const [showHistory, setShowHistory] = useState(false);
  const [showGoals, setShowGoals] = useState(false);
  
  // Print function
  const handlePrint = () => {
    // Create print-friendly content
    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="UTF-8">
        <title>Analyse Transversale - ${user_name} - REVELIAE</title>
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body { 
            font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
            padding: 40px;
            color: #1f2937;
            line-height: 1.6;
          }
          .header { 
            text-align: center;
            margin-bottom: 30px;
            padding-bottom: 20px;
            border-bottom: 3px solid #8b5cf6;
          }
          .logo { font-size: 28px; color: #8b5cf6; font-weight: bold; margin-bottom: 10px; }
          .title { font-size: 24px; color: #374151; margin-bottom: 5px; }
          .subtitle { font-size: 14px; color: #6b7280; }
          .profile-badges {
            display: flex;
            justify-content: center;
            gap: 15px;
            margin-top: 15px;
            flex-wrap: wrap;
          }
          .badge {
            background: #f3e8ff;
            color: #7c3aed;
            padding: 8px 16px;
            border-radius: 20px;
            font-size: 14px;
            font-weight: 500;
          }
          .section { 
            margin-bottom: 25px;
            page-break-inside: avoid;
          }
          .section-title { 
            font-size: 18px;
            color: #7c3aed;
            margin-bottom: 12px;
            padding-bottom: 8px;
            border-bottom: 2px solid #e9d5ff;
            display: flex;
            align-items: center;
            gap: 8px;
          }
          .section-content {
            background: #faf5ff;
            padding: 16px;
            border-radius: 12px;
            border-left: 4px solid #8b5cf6;
          }
          .energy-grid {
            display: grid;
            grid-template-columns: repeat(3, 1fr);
            gap: 15px;
            margin-top: 15px;
          }
          .energy-item {
            background: white;
            border: 1px solid #e5e7eb;
            border-radius: 10px;
            padding: 15px;
            text-align: center;
          }
          .energy-value {
            font-size: 28px;
            font-weight: bold;
            color: #8b5cf6;
          }
          .energy-label {
            font-size: 13px;
            color: #6b7280;
            margin-top: 5px;
          }
          .insight-card {
            background: white;
            border: 1px solid #e5e7eb;
            border-radius: 12px;
            padding: 16px;
            margin-bottom: 12px;
          }
          .insight-title {
            font-weight: 600;
            color: #374151;
            margin-bottom: 8px;
          }
          .insight-text {
            color: #4b5563;
            font-size: 14px;
          }
          .footer {
            margin-top: 40px;
            padding-top: 20px;
            border-top: 1px solid #e5e7eb;
            text-align: center;
            color: #9ca3af;
            font-size: 12px;
          }
          @media print {
            body { padding: 20px; }
            .section { page-break-inside: avoid; }
          }
        </style>
      </head>
      <body>
        <div class="header">
          <div class="logo">✨ REVELIAE</div>
          <div class="title">Analyse Transversale de ${user_name}</div>
          <div class="subtitle">${date}</div>
          <div class="profile-badges">
            <span class="badge">${profile?.zodiac?.symbol || ''} ${profile?.zodiac?.name || 'Signe'}</span>
            <span class="badge">Chemin de vie ${profile?.life_path?.number || '?'}</span>
            <span class="badge">Année personnelle ${profile?.personal_year?.number || '?'}</span>
            <span class="badge">Élément ${element}</span>
          </div>
        </div>

        <div class="section">
          <div class="section-title">📊 Scores Énergétiques</div>
          <div class="energy-grid">
            ${energy_scores ? Object.entries(energy_scores).map(([key, value]) => `
              <div class="energy-item">
                <div class="energy-value">${Math.round(value)}</div>
                <div class="energy-label">${ATTR_NAMES[key] || key}</div>
              </div>
            `).join('') : ''}
          </div>
        </div>

        <div class="section">
          <div class="section-title">🌟 Profil Astrologique</div>
          <div class="section-content">
            <p><strong>Signe solaire :</strong> ${profile?.zodiac?.name || 'N/A'} - ${profile?.zodiac?.description || ''}</p>
            <p style="margin-top: 10px;"><strong>Élément :</strong> ${element}</p>
          </div>
        </div>

        <div class="section">
          <div class="section-title">🔢 Profil Numérologique</div>
          <div class="section-content">
            <p><strong>Chemin de vie ${profile?.life_path?.number} :</strong> ${profile?.life_path?.description || ''}</p>
            <p style="margin-top: 10px;"><strong>Année personnelle ${profile?.personal_year?.number} :</strong> ${profile?.personal_year?.meaning || ''}</p>
          </div>
        </div>

        ${content?.synthesis ? `
        <div class="section">
          <div class="section-title">🎯 Synthèse</div>
          <div class="section-content">
            <p>${content.synthesis}</p>
          </div>
        </div>
        ` : ''}

        ${content?.strengths ? `
        <div class="section">
          <div class="section-title">💪 Forces</div>
          <div class="section-content">
            <p>${content.strengths}</p>
          </div>
        </div>
        ` : ''}

        ${content?.challenges ? `
        <div class="section">
          <div class="section-title">⚡ Défis</div>
          <div class="section-content">
            <p>${content.challenges}</p>
          </div>
        </div>
        ` : ''}

        ${content?.advice ? `
        <div class="section">
          <div class="section-title">💡 Conseils</div>
          <div class="section-content">
            <p>${content.advice}</p>
          </div>
        </div>
        ` : ''}

        ${content?.period_guidance ? `
        <div class="section">
          <div class="section-title">📅 Guidance pour la période</div>
          <div class="section-content">
            <p>${content.period_guidance}</p>
          </div>
        </div>
        ` : ''}

        <div class="footer">
          <p>© 2026 REVELIAE - Arts divinatoires par IA</p>
          <p style="margin-top: 5px;">Document généré le ${new Date().toLocaleDateString('fr-FR', { 
            weekday: 'long', 
            year: 'numeric', 
            month: 'long', 
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
          })}</p>
        </div>
      </body>
      </html>
    `;
    
    // Secure print method using Blob instead of document.write (XSS prevention)
    const blob = new Blob([printContent], { type: 'text/html;charset=utf-8' });
    const printUrl = URL.createObjectURL(blob);
    const printWindow = window.open(printUrl, '_blank');
    
    if (printWindow) {
      printWindow.onload = () => {
        printWindow.focus();
        setTimeout(() => {
          printWindow.print();
          // Clean up the blob URL after printing
          URL.revokeObjectURL(printUrl);
        }, 250);
      };
    }
  };
  
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="max-w-5xl mx-auto space-y-6"
    >
      {/* Energy Alert Banner */}
      {alerts && alerts.length > 0 && (
        <EnergyAlertBanner 
          alerts={alerts} 
          onDismiss={onDismissAlert}
        />
      )}
      
      {/* Header Card */}
      <div className="bg-gradient-to-br from-violet-600 via-purple-600 to-indigo-700 rounded-3xl p-8 text-white shadow-2xl">
        <div className="flex flex-col md:flex-row items-center gap-6">
          <div className="flex items-center gap-4">
            <motion.div 
              className="text-6xl"
              animate={{ scale: [1, 1.05, 1] }}
              transition={{ duration: 2, repeat: Infinity }}
            >
              {profile?.zodiac?.symbol || '?'}
            </motion.div>
            <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center text-3xl font-bold backdrop-blur-sm">
              {profile?.life_path?.number || '?'}
            </div>
          </div>
          <div className="text-center md:text-left flex-1">
            <p className="text-purple-200 text-sm mb-1">{date}</p>
            <h2 className="text-2xl md:text-3xl font-bold mb-3">
              Analyse Transversale de {user_name}
            </h2>
            <div className="flex flex-wrap gap-2 justify-center md:justify-start">
              <span className="px-4 py-1.5 bg-white/20 rounded-full text-sm backdrop-blur-sm">
                {profile?.zodiac?.name || 'Signe'} {profile?.zodiac?.symbol}
              </span>
              <span className="px-4 py-1.5 bg-white/20 rounded-full text-sm backdrop-blur-sm">
                Chemin de vie {profile?.life_path?.number}
              </span>
              <span className="px-4 py-1.5 bg-white/20 rounded-full text-sm backdrop-blur-sm">
                Annee {profile?.personal_year?.number}
              </span>
              <ElementBadge element={element} />
            </div>
          </div>
          <div className="flex flex-col gap-2">
            <Button
              onClick={onRefresh}
              variant="ghost"
              className="bg-white/10 hover:bg-white/20 text-white"
              data-testid="new-analysis-btn"
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Nouvelle analyse
            </Button>
            {history && history.length > 0 && (
              <Button
                onClick={() => setShowHistory(!showHistory)}
                variant="ghost"
                className="bg-white/10 hover:bg-white/20 text-white"
                data-testid="history-toggle-btn"
              >
                <History className="h-4 w-4 mr-2" />
                Historique ({history.length})
                {showHistory ? <ChevronUp className="h-4 w-4 ml-1" /> : <ChevronDown className="h-4 w-4 ml-1" />}
              </Button>
            )}
            <Button
              onClick={() => setShowGoals(!showGoals)}
              variant="ghost"
              className="bg-white/10 hover:bg-white/20 text-white"
              data-testid="goals-toggle-btn"
            >
              <Trophy className="h-4 w-4 mr-2" />
              Objectifs
              {goals && goals.length > 0 && (
                <span className="ml-1 px-1.5 py-0.5 bg-amber-400 text-amber-900 rounded-full text-xs font-bold">
                  {goals.length}
                </span>
              )}
            </Button>
            <Button
              onClick={handlePrint}
              variant="ghost"
              className="bg-white/10 hover:bg-white/20 text-white"
              data-testid="print-analysis-btn"
            >
              <Printer className="h-4 w-4 mr-2" />
              Imprimer
            </Button>
          </div>
        </div>
      </div>

      {/* History Panel */}
      <AnimatePresence>
        {showHistory && history && history.length > 0 && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="overflow-hidden space-y-4"
          >
            <EvolutionChart history={history} />
            
            {/* History List with Delete */}
            <div className="bg-white rounded-2xl border border-gray-200 p-6 shadow-lg">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-3 bg-gradient-to-br from-indigo-100 to-purple-100 rounded-xl">
                  <Clock className="h-6 w-6 text-indigo-600" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900">Historique des Analyses</h3>
                  <p className="text-sm text-gray-500">Clique sur une analyse pour la revoir</p>
                </div>
              </div>
              <HistoryList 
                history={history} 
                onSelect={onSelectAnalysis}
                onDelete={onDelete}
                selectedId={null}
              />
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Goals Panel */}
      <AnimatePresence>
        {showGoals && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="overflow-hidden"
          >
            <EnergyGoalsPanel
              goals={goals}
              achievements={achievements}
              currentScores={energy_scores}
              onCreateGoal={onCreateGoal}
              onDeleteGoal={onDeleteGoal}
            />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <EnergyRadarChart profile={profile} energyScores={energy_scores} />
        <YearCycleTimeline profile={profile} />
      </div>

      {/* Snapshot Section */}
      <div className="bg-white rounded-2xl border border-gray-200 p-6 shadow-lg">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-3 bg-amber-100 rounded-xl">
            <Eye className="h-6 w-6 text-amber-600" />
          </div>
          <h3 className="text-xl font-bold text-gray-900">{content?.snapshot?.title}</h3>
        </div>
        
        <p className="text-gray-600 mb-6 leading-relaxed text-lg">{content?.snapshot?.summary}</p>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="p-4 bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl border border-green-200">
            <h4 className="font-semibold text-green-800 mb-3 flex items-center gap-2">
              <Sparkles className="h-4 w-4" /> Tes Forces
            </h4>
            <ul className="space-y-2">
              {content?.snapshot?.strengths?.map((s, i) => (
                <motion.li 
                  key={i} 
                  className="flex items-center gap-2 text-green-700"
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: i * 0.1 }}
                >
                  <Star className="h-4 w-4 text-green-500" /> {s}
                </motion.li>
              ))}
            </ul>
          </div>
          
          <div className="p-4 bg-gradient-to-br from-amber-50 to-orange-50 rounded-xl border border-amber-200">
            <h4 className="font-semibold text-amber-800 mb-3 flex items-center gap-2">
              <Target className="h-4 w-4" /> Tes Defis
            </h4>
            <ul className="space-y-2">
              {content?.snapshot?.challenges?.map((c, i) => (
                <motion.li 
                  key={i} 
                  className="flex items-center gap-2 text-amber-700"
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: i * 0.1 + 0.3 }}
                >
                  <Zap className="h-4 w-4 text-amber-500" /> {c}
                </motion.li>
              ))}
            </ul>
          </div>
        </div>
        
        <div className="mt-4 p-4 bg-gradient-to-r from-purple-50 via-violet-50 to-indigo-50 rounded-xl border border-purple-200">
          <p className="text-purple-800">{content?.snapshot?.energy_profile}</p>
        </div>
      </div>

      {/* Dynamics Section */}
      <div className="bg-white rounded-2xl border border-gray-200 p-6 shadow-lg">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-3 bg-blue-100 rounded-xl">
            <Compass className="h-6 w-6 text-blue-600" />
          </div>
          <h3 className="text-xl font-bold text-gray-900">{content?.dynamics?.title}</h3>
        </div>
        
        <div className="space-y-4">
          <div className="p-4 bg-gradient-to-r from-blue-50 to-sky-50 rounded-xl border border-blue-200">
            <h4 className="font-semibold text-blue-800 mb-2">Phase Actuelle</h4>
            <p className="text-blue-700">{content?.dynamics?.current_phase}</p>
          </div>
          
          <div className="p-4 bg-gradient-to-r from-emerald-50 to-teal-50 rounded-xl border border-emerald-200">
            <h4 className="font-semibold text-emerald-800 mb-2">Opportunites</h4>
            <div className="flex flex-wrap gap-2">
              {content?.dynamics?.opportunities?.map((o, i) => (
                <motion.span 
                  key={i} 
                  className="px-4 py-2 bg-emerald-100 text-emerald-700 rounded-full text-sm font-medium"
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: i * 0.1 }}
                >
                  {o}
                </motion.span>
              ))}
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="p-4 bg-gradient-to-br from-sky-50 to-cyan-50 rounded-xl border border-sky-200">
              <h4 className="font-semibold text-sky-800 mb-2 flex items-center gap-2">
                <Calendar className="h-4 w-4" /> Ce Mois-ci
              </h4>
              <p className="text-sky-700 text-sm">{content?.dynamics?.advice_month}</p>
            </div>
            <div className="p-4 bg-gradient-to-br from-indigo-50 to-violet-50 rounded-xl border border-indigo-200">
              <h4 className="font-semibold text-indigo-800 mb-2 flex items-center gap-2">
                <TrendingUp className="h-4 w-4" /> Tendance 6 Mois
              </h4>
              <p className="text-indigo-700 text-sm">{content?.dynamics?.long_term_trend}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Synergy Section */}
      <div className="bg-white rounded-2xl border border-gray-200 p-6 shadow-lg">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-3 bg-rose-100 rounded-xl">
            <Heart className="h-6 w-6 text-rose-600" />
          </div>
          <h3 className="text-xl font-bold text-gray-900">{content?.synergy?.title}</h3>
        </div>
        
        <div className="space-y-4">
          <div className="p-4 bg-gradient-to-r from-amber-50 via-yellow-50 to-emerald-50 rounded-xl border border-amber-200">
            <div className="flex items-center gap-2 mb-2">
              <Sun className="h-5 w-5 text-amber-500" />
              <Hash className="h-5 w-5 text-emerald-500" />
            </div>
            <p className="text-gray-700">{content?.synergy?.astro_numero_link}</p>
          </div>
          
          <div 
            className="p-4 rounded-xl border"
            style={{ 
              backgroundColor: `${elementConfig.primary}10`,
              borderColor: `${elementConfig.primary}30`
            }}
          >
            <div className="flex items-center gap-2 mb-2">
              <elementConfig.icon className="h-5 w-5" style={{ color: elementConfig.primary }} />
              <span className="font-semibold" style={{ color: elementConfig.primary }}>
                Element {element}
              </span>
            </div>
            <p className="text-gray-700">{content?.synergy?.element_influence}</p>
          </div>
          
          <motion.div 
            className="p-5 bg-gradient-to-r from-violet-100 via-purple-100 to-indigo-100 rounded-xl border border-purple-300"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
          >
            <p className="text-purple-800 font-medium text-lg italic text-center">
              "{content?.synergy?.spiritual_message}"
            </p>
          </motion.div>
        </div>
      </div>

      {/* Daily Guidance */}
      <motion.div 
        className="bg-gradient-to-r from-violet-500 via-purple-500 to-indigo-500 rounded-2xl p-8 text-white shadow-xl"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
      >
        <h3 className="text-2xl font-bold mb-6 text-center">Ta Guidance du Jour</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <motion.div 
            className="text-center p-5 bg-white/10 rounded-2xl backdrop-blur-sm"
            whileHover={{ scale: 1.02 }}
          >
            <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-3">
              <Target className="h-6 w-6" />
            </div>
            <p className="text-purple-200 text-xs uppercase tracking-wider mb-2">Focus</p>
            <p className="text-2xl font-bold">{content?.daily_guidance?.focus}</p>
          </motion.div>
          <motion.div 
            className="text-center p-5 bg-white/10 rounded-2xl backdrop-blur-sm"
            whileHover={{ scale: 1.02 }}
          >
            <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-3">
              <Zap className="h-6 w-6" />
            </div>
            <p className="text-purple-200 text-xs uppercase tracking-wider mb-2">Action</p>
            <p className="text-sm font-medium">{content?.daily_guidance?.action}</p>
          </motion.div>
          <motion.div 
            className="text-center p-5 bg-white/10 rounded-2xl backdrop-blur-sm"
            whileHover={{ scale: 1.02 }}
          >
            <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-3">
              <Sparkles className="h-6 w-6" />
            </div>
            <p className="text-purple-200 text-xs uppercase tracking-wider mb-2">Affirmation</p>
            <p className="text-sm italic">"{content?.daily_guidance?.affirmation}"</p>
          </motion.div>
        </div>
      </motion.div>
    </motion.div>
  );
};

// Main Page Component
const TransversalAnalysisPage = () => {
  const navigate = useNavigate();
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const [analysisData, setAnalysisData] = useState(null);
  const [history, setHistory] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [goals, setGoals] = useState([]);
  const [achievements, setAchievements] = useState([]);
  const [celebratingAchievement, setCelebratingAchievement] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const fetchAlerts = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/analysis/alerts`, {
        withCredentials: true
      });
      if (response.data.success) {
        setAlerts(response.data.alerts);
      }
    } catch (err) {
      console.error('Error fetching alerts:', err);
    }
  };
  
  const fetchGoals = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/analysis/goals`, {
        withCredentials: true
      });
      if (response.data.success) {
        setGoals(response.data.goals);
        setAchievements(response.data.achievements);
      }
    } catch (err) {
      console.error('Error fetching goals:', err);
    }
  };
  
  const handleCreateGoal = async (goalData) => {
    try {
      const response = await axios.post(`${API_URL}/api/analysis/goals`, goalData, {
        withCredentials: true
      });
      if (response.data.success) {
        fetchGoals();
      }
      return response.data;
    } catch (err) {
      console.error('Error creating goal:', err);
      return { success: false };
    }
  };
  
  const handleDeleteGoal = async (goalId) => {
    try {
      const response = await axios.delete(`${API_URL}/api/analysis/goals/${goalId}`, {
        withCredentials: true
      });
      if (response.data.success) {
        setGoals(prev => prev.filter(g => g.goal_id !== goalId));
      }
    } catch (err) {
      console.error('Error deleting goal:', err);
    }
  };
  
  const checkGoalsProgress = async () => {
    try {
      const response = await axios.post(`${API_URL}/api/analysis/goals/check`, {}, {
        withCredentials: true
      });
      if (response.data.success && response.data.new_achievements?.length > 0) {
        // Celebrate first achievement
        setCelebratingAchievement(response.data.new_achievements[0]);
        // Refresh goals
        fetchGoals();
      }
    } catch (err) {
      console.error('Error checking goals:', err);
    }
  };
  
  const fetchAnalysis = async (save = true) => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await axios.get(`${API_URL}/api/analysis/transversal?save=${save}`, {
        withCredentials: true
      });
      setAnalysisData(response.data);
      
      // Also fetch history, alerts and goals
      if (response.data.success && response.data.is_premium) {
        fetchHistory();
        fetchAlerts();
        fetchGoals();
        // Check goals after new analysis
        setTimeout(() => checkGoalsProgress(), 1000);
      }
    } catch (err) {
      console.error('Error fetching analysis:', err);
      setError(err.response?.data?.detail || 'Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };
  
  const fetchHistory = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/analysis/transversal/history`, {
        withCredentials: true
      });
      if (response.data.success) {
        setHistory(response.data.history);
      }
    } catch (err) {
      console.error('Error fetching history:', err);
    }
  };
  
  const handleRefresh = () => {
    fetchAnalysis(true);
  };
  
  const handleDeleteAnalysis = async (analysisId) => {
    try {
      const response = await axios.delete(`${API_URL}/api/analysis/transversal/${analysisId}`, {
        withCredentials: true
      });
      if (response.data.success) {
        // Remove from local state
        setHistory(prev => prev.filter(h => h.analysis_id !== analysisId));
      }
    } catch (err) {
      console.error('Error deleting analysis:', err);
    }
  };
  
  const handleSelectAnalysis = async (analysisId) => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/analysis/transversal/${analysisId}`, {
        withCredentials: true
      });
      if (response.data.success && response.data.analysis) {
        setAnalysisData(response.data);
      }
    } catch (err) {
      console.error('Error loading analysis:', err);
    } finally {
      setLoading(false);
    }
  };
  
  const handleDismissAlert = async (alertId) => {
    try {
      const response = await axios.post(`${API_URL}/api/analysis/alerts/${alertId}/dismiss`, {}, {
        withCredentials: true
      });
      if (response.data.success) {
        setAlerts(prev => prev.filter(a => a.alert_id !== alertId));
      }
    } catch (err) {
      console.error('Error dismissing alert:', err);
    }
  };
  
  useEffect(() => {
    if (!authLoading) {
      fetchAnalysis();
    }
  }, [authLoading]);
  
  const renderContent = () => {
    if (authLoading || loading) {
      return <LoadingState />;
    }
    
    if (error) {
      return (
        <div className="text-center py-12">
          <p className="text-red-500 mb-4">{error}</p>
          <Button onClick={() => fetchAnalysis()} variant="outline">
            <RefreshCw className="h-4 w-4 mr-2" />
            Reessayer
          </Button>
        </div>
      );
    }
    
    if (!analysisData) {
      return <LoadingState />;
    }
    
    if (!analysisData.success && !analysisData.is_premium) {
      if (analysisData.message?.includes('Connexion')) {
        navigate('/auth');
        return null;
      }
      return <PremiumUpsell onNavigate={navigate} />;
    }
    
    if (analysisData.success && analysisData.is_premium && !analysisData.analysis) {
      return <ProfilePrompt message={analysisData.message} onNavigate={navigate} />;
    }
    
    if (analysisData.analysis) {
      return <AnalysisContent 
        analysis={analysisData.analysis} 
        history={history} 
        onRefresh={handleRefresh}
        onDelete={handleDeleteAnalysis}
        onSelectAnalysis={handleSelectAnalysis}
        alerts={alerts}
        onDismissAlert={handleDismissAlert}
        goals={goals}
        achievements={achievements}
        onCreateGoal={handleCreateGoal}
        onDeleteGoal={handleDeleteGoal}
      />;
    }
    
    return <PremiumUpsell onNavigate={navigate} />;
  };
  
  return (
    <div className="min-h-screen bg-gradient-to-b from-violet-50 via-white to-purple-50">
      {/* Achievement Celebration Modal */}
      <AnimatePresence>
        {celebratingAchievement && (
          <AchievementCelebration 
            achievement={celebratingAchievement}
            onClose={() => setCelebratingAchievement(null)}
          />
        )}
      </AnimatePresence>
      
      {/* Decorative */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 right-20 w-64 h-64 bg-violet-200/30 rounded-full blur-3xl" />
        <div className="absolute bottom-20 left-20 w-80 h-80 bg-purple-200/20 rounded-full blur-3xl" />
        <div className="absolute top-1/2 left-1/3 w-96 h-96 bg-indigo-200/10 rounded-full blur-3xl" />
      </div>
      
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-md border-b border-purple-100">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link 
            to="/" 
            className="flex items-center gap-2 text-purple-600 hover:text-purple-700 transition-colors"
            data-testid="back-to-home"
          >
            <ArrowLeft className="h-5 w-5" />
            <span className="font-semibold">REVELIAE</span>
          </Link>
          
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2 px-4 py-1.5 bg-gradient-to-r from-violet-100 to-purple-100 rounded-full border border-purple-200">
              <Crown className="h-4 w-4 text-purple-600" />
              <span className="text-purple-700 text-sm font-medium">Integral</span>
            </div>
            {history.length > 0 && (
              <div className="flex items-center gap-1 px-3 py-1.5 bg-indigo-100 rounded-full">
                <History className="h-4 w-4 text-indigo-600" />
                <span className="text-indigo-700 text-sm font-medium">{history.length}</span>
              </div>
            )}
          </div>
        </div>
      </header>
      
      {/* Hero */}
      <section className="relative py-12 px-4">
        <div className="max-w-4xl mx-auto text-center mb-12">
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="inline-flex items-center gap-3 px-6 py-3 bg-gradient-to-r from-violet-100 via-purple-100 to-indigo-100 rounded-full border border-purple-200 mb-6 shadow-sm">
              <Sun className="h-5 w-5 text-amber-500" />
              <Hash className="h-5 w-5 text-emerald-500" />
              <Moon className="h-5 w-5 text-indigo-500" />
              <Activity className="h-5 w-5 text-purple-500" />
              <TrendingUp className="h-5 w-5 text-blue-500" />
            </div>
            
            <h1 className="text-4xl sm:text-5xl font-bold mb-4">
              <span className="text-gray-900">Analyse</span>
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-violet-600 via-purple-600 to-indigo-600 ml-3">
                Transversale
              </span>
            </h1>
            
            <p className="text-gray-600 text-lg max-w-2xl mx-auto leading-relaxed">
              Une vision holistique combinant astrologie, numerologie et cycles personnels
              avec suivi de ton evolution sur plusieurs mois.
            </p>
          </motion.div>
        </div>
        
        {/* Main Content */}
        <div className="px-4 pb-16">
          {renderContent()}
        </div>
      </section>
    </div>
  );
};

export default TransversalAnalysisPage;
